"use client"

import { useSession } from 'next-auth/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { trpc } from '@plura/shared/trpc';
import {
  captureFirstTouchAttribution,
  events,
  identify as identifyPostHog,
  initBrowserPostHog,
  isTrackingExcludedPath,
  resetIdentity,
  track as trackBrowser,
  UserEventType,
  EventStage,
  type GrowthStage,
  type TrackProperties,
} from '@plura/shared/track';
import { isExcludedTrackingEmail } from '@plura/shared/posthog';
import { FormJourneyQuestionType, LeadFormTemplateType } from '@plura/shared/types';

/**
 * useTrack — unified analytics hook.
 *
 * Every event is mirrored to:
 *
 *   1. **PostHog** (primary product analytics + agent context store)
 *   2. **GTM dataLayer** (legacy)
 *   3. **Postgres `UserEvent`** via tRPC (source of truth for billing-critical
 *      events and the admin dashboard's per-user timeline)
 *
 * Prefer the generic `track(name, props, opts)` for new code. The named
 * helpers (`trackFormCreated`, `trackOpenBillingModal`, ...) are kept for
 * backwards compatibility with existing call sites.
 *
 * All functions are memoised so they're safe to use as `useEffect`
 * dependencies.
 */
export function useTrack() {
  const { data: session } = useSession();
  const userId = session?.user?.id;
  const defaultWorkspaceId = session?.user?.workspace;
  const userEmail = (session?.user as { email?: string } | undefined)?.email;
  const userName = (session?.user as { name?: string } | undefined)?.name;

  const userIdRef = useRef(userId);
  const defaultWorkspaceIdRef = useRef(defaultWorkspaceId);
  const isExcludedUserRef = useRef(false);
  userIdRef.current = userId;
  defaultWorkspaceIdRef.current = defaultWorkspaceId;
  isExcludedUserRef.current = isExcludedTrackingEmail(userEmail);

  // Initialise PostHog + identify on mount so subsequent captures attach to a
  // logical user, not just a cookie distinct id. Skipped on operator routes
  // so admin clicks never appear in product analytics.
  useEffect(() => {
    if (isTrackingExcludedPath()) return;
    void initBrowserPostHog();
    // First-touch attribution: no-op when the visitor already carries an
    // fs_attr cookie (e.g. set on the landing site); otherwise captures
    // direct arrivals to the app (ads pointing straight at /sign-up).
    captureFirstTouchAttribution();
  }, []);

  useEffect(() => {
    if (isTrackingExcludedPath()) return;
    if (userId) {
      // identify() is ALSO responsible for opting the user out of capturing
      // when their email is in the excluded list — call it even for those
      // users so the SDK can suppress autocapture.
      identifyPostHog({
        userId,
        email: userEmail ?? undefined,
        name: userName ?? undefined,
        workspaceId: defaultWorkspaceId,
      });
    } else {
      resetIdentity();
    }
  }, [userId, userEmail, userName, defaultWorkspaceId]);

  const trackMutation = trpc.user.trackEvent.useMutation({
    onError: (error) => {
      console.warn('[useTrack] Failed to store event:', error.message);
    },
  });
  const trpcUtils = trpc.useUtils();

  const mutateRef = useRef(trackMutation.mutate);
  mutateRef.current = trackMutation.mutate;

  /**
   * Generic event capture. Sends to PostHog + GTM + Postgres in one call.
   *
   * @example
   *   const { track } = useTrack();
   *   track('paywall_shown', { feature: 'ai_chat', plan_required: 'PRO' });
   */
  const track = useCallback(
    (
      eventName: UserEventType,
      properties: TrackProperties = {},
      options: { workspaceId?: string; stage?: GrowthStage; skipDb?: boolean } = {}
    ) => {
      // Skip ALL tracking on excluded routes (admin / operator surfaces). This
      // prevents browser capture, GTM dataLayer, and the Postgres `UserEvent`
      // mirror from firing.
      if (isTrackingExcludedPath()) return;
      // Skip ALL tracking for internal/operator emails (e.g. support@). They
      // muddy product funnels and retention curves, and the support team
      // doesn't need a PostHog timeline.
      if (isExcludedUserRef.current) return;

      const stage = options.stage ?? EventStage[eventName];
      const eventLower = eventName.toLowerCase();
      const ws = options.workspaceId ?? defaultWorkspaceIdRef.current;

      trackBrowser(eventLower, properties, { workspaceId: ws, stage });

      if (userIdRef.current && !options.skipDb) {
        mutateRef.current({
          eventName,
          workspaceId: ws,
          metadata: properties as Record<string, unknown>,
        });
      }
    },
    []
  );

  // ─── Auth / Account ──────────────────────────────────────────────────────
  const trackSignup = useCallback((data: { email: string; name: string }) => {
    if (userIdRef.current) {
      events.signup(userIdRef.current, data);
      track('SIGNUP', { email: data.email, name: data.name });
    }
  }, [track]);

  const trackSignUpFormOpened = useCallback(() => {
    track('SIGN_UP_FORM_OPENED');
  }, [track]);

  const trackSignupStarted = useCallback((provider: 'email' | 'google' | string) => {
    track('SIGNUP_STARTED', { provider });
  }, [track]);

  const trackSignupValidationError = useCallback((field: string, reason: string) => {
    track('SIGNUP_VALIDATION_ERROR', { field, reason });
  }, [track]);

  const trackSignInStarted = useCallback((provider: 'email' | 'google' | string) => {
    track('SIGN_IN_STARTED', { provider });
  }, [track]);

  const trackSignInCompleted = useCallback((provider: 'email' | 'google' | string) => {
    track('SIGN_IN_COMPLETED', { provider });
  }, [track]);

  const trackSignInFailed = useCallback(
    (provider: 'email' | 'google' | string, reason: string) => {
      track('SIGN_IN_FAILED', { provider, reason });
    },
    [track]
  );

  const trackEmailVerified = useCallback(() => {
    track('EMAIL_VERIFIED');
  }, [track]);

  const trackResetPasswordRequested = useCallback(() => {
    track('RESET_PASSWORD_REQUESTED');
  }, [track]);

  const trackResetPasswordSuccess = useCallback(() => {
    track('RESET_PASSWORD_SUCCESS');
  }, [track]);

  const trackWorkspaceInviteAccepted = useCallback((workspaceId?: string) => {
    track('WORKSPACE_INVITE_ACCEPTED', {}, { workspaceId });
  }, [track]);

  // ─── Onboarding ──────────────────────────────────────────────────────────
  const trackOnboardingStepViewed = useCallback(
    (step: string, blockType?: string) => {
      track('ONBOARDING_STEP_VIEWED', { step, block_type: blockType });
    },
    [track]
  );

  const trackOnboardingStepAnswered = useCallback(
    (step: string, answer: string | string[]) => {
      track('ONBOARDING_STEP_ANSWERED', {
        step,
        answer: Array.isArray(answer) ? answer.join(',') : answer,
      });
    },
    [track]
  );

  const trackOnboardingCompleted = useCallback(
    (props: {
      purpose?: string;
      role?: string;
      goals?: string[];
      hasWebsite?: boolean;
      hearAboutUs?: string;
    }) => {
      track('ONBOARDING_COMPLETED', {
        purpose: props.purpose,
        role: props.role,
        goals: props.goals?.join(','),
        has_website: props.hasWebsite,
        hear_about_us: props.hearAboutUs,
      });
    },
    [track]
  );

  const trackOnboardingSkipped = useCallback((workspaceId?: string) => {
    track('ONBOARDING_SKIPPED', {}, { workspaceId });
  }, [track]);

  // ─── Editor ──────────────────────────────────────────────────────────────
  const trackEditorFirstEntered = useCallback(
    (collectionId: string, workspaceId?: string) => {
      track('EDITOR_FIRST_ENTERED', { collection_id: collectionId }, { workspaceId });
    },
    [track]
  );

  const trackEditorBouncedWithoutSave = useCallback(
    (collectionId: string, msSpent: number, workspaceId?: string) => {
      track(
        'EDITOR_BOUNCED_WITHOUT_SAVE',
        { collection_id: collectionId, ms_spent: msSpent },
        { workspaceId }
      );
    },
    [track]
  );

  const trackEditorGetStartedIntro = useCallback((workspaceId?: string) => {
    track('EDITOR_GET_STARTED_INTRO', {}, { workspaceId });
  }, [track]);

  const trackEditorGetStartedIntroSkipped = useCallback((workspaceId?: string) => {
    track('EDITOR_GET_STARTED_INTRO_SKIPPED', {}, { workspaceId });
  }, [track]);

  const trackEditorGetStartedIntroVideoViewed = useCallback(
    (video: string, workspaceId?: string) => {
      track('EDITOR_GET_STARTED_INTRO_VIDEO_VIEWED', { video }, { workspaceId });
    },
    [track]
  );

  const trackHowToTooltipOpened = useCallback(
    (tooltipName: string, workspaceId?: string) => {
      track('HOW_TO_TOOLTIP_OPENED', { tooltip_name: tooltipName }, { workspaceId });
    },
    [track]
  );

  const trackHowToTooltipVideoViewed = useCallback(
    (tooltipName: string, workspaceId?: string) => {
      track('HOW_TO_TOOLTIP_VIDEO_VIEWED', { tooltip_name: tooltipName }, { workspaceId });
    },
    [track]
  );

  // ─── Form lifecycle ──────────────────────────────────────────────────────
  const trackFormCreated = useCallback(
    (data: { collectionId: string; workspaceId?: string; template?: LeadFormTemplateType }) => {
      track(
        'FORM_CREATED',
        { collection_id: data.collectionId, template: data.template },
        { workspaceId: data.workspaceId }
      );
    },
    [track]
  );

  const trackFormDraftSaved = useCallback(
    (collectionId: string, workspaceId?: string) => {
      track('FORM_DRAFT_SAVED', { collection_id: collectionId }, { workspaceId });
    },
    [track]
  );

  const trackFormPublished = useCallback(
    (collectionId: string, workspaceId?: string) => {
      track('FORM_PUBLISHED', { collection_id: collectionId }, { workspaceId });
    },
    [track]
  );

  const trackFormFirstPublished = useCallback(
    (collectionId: string, workspaceId?: string) => {
      track('FORM_FIRST_PUBLISHED', { collection_id: collectionId }, { workspaceId });
    },
    [track]
  );

  const trackFormEditedAfterPublish = useCallback(
    (collectionId: string, workspaceId?: string) => {
      track('FORM_EDITED_AFTER_PUBLISH', { collection_id: collectionId }, { workspaceId });
    },
    [track]
  );

  const trackAddJourneyItem = useCallback(
    (type: FormJourneyQuestionType, workspaceId?: string) => {
      track('ADD_JOURNEY_ITEM', { type }, { workspaceId });
    },
    [track]
  );

  const trackEditJourneyItem = useCallback(
    (workspaceId?: string) => {
      track('EDIT_JOURNEY_ITEM', {}, { workspaceId });
    },
    [track]
  );

  const trackQuestionEdited = useCallback(
    (input: { collectionId: string; journeyItemId: string; field: string; workspaceId?: string }) => {
      track(
        'QUESTION_EDITED',
        {
          collection_id: input.collectionId,
          journey_item_id: input.journeyItemId,
          field: input.field,
        },
        { workspaceId: input.workspaceId }
      );
    },
    [track]
  );

  // ─── Sharing ─────────────────────────────────────────────────────────────
  const trackOpenShareFormModal = useCallback(
    (collectionId: string, workspaceId?: string) => {
      track('OPEN_SHARE_FORM_MODAL', { collection_id: collectionId }, { workspaceId });
    },
    [track]
  );

  /**
   * The FirstResponseJourney tracker's "Share your form" step reads the two
   * share events below. `track()` fire-and-forgets the UserEvent write, so
   * nudge the journey query shortly after — long enough for the mutation to
   * land — and the step ticks live instead of waiting for a refresh.
   */
  const refreshActivationJourney = useCallback(() => {
    window.setTimeout(() => {
      void trpcUtils.workspace.getActivationJourney.invalidate();
    }, 1_500);
  }, [trpcUtils]);

  const trackFormSharedViaLink = useCallback(
    (input: { collectionId: string; method: string; workspaceId?: string }) => {
      track(
        'FORM_SHARED_VIA_LINK',
        { collection_id: input.collectionId, method: input.method },
        { workspaceId: input.workspaceId }
      );
      refreshActivationJourney();
    },
    [track, refreshActivationJourney]
  );

  const trackFormEmbedCodeCopied = useCallback(
    (input: { collectionId: string; embedType: string; workspaceId?: string }) => {
      track(
        'FORM_EMBED_CODE_COPIED',
        { collection_id: input.collectionId, embed_type: input.embedType },
        { workspaceId: input.workspaceId }
      );
      refreshActivationJourney();
    },
    [track, refreshActivationJourney]
  );

  // ─── AI ──────────────────────────────────────────────────────────────────
  const trackOpenAiAssistantModal = useCallback((workspaceId?: string) => {
    track('OPEN_AI_ASSISTANT_MODAL', {}, { workspaceId });
  }, [track]);

  const trackCreateFormWithAiOpened = useCallback((workspaceId?: string) => {
    track('CREATE_FORM_WITH_AI_OPENED', {}, { workspaceId });
  }, [track]);

  const trackCreateFormWithAiSubmitted = useCallback(
    (prompt: string, workspaceId?: string) => {
      track('CREATE_FORM_WITH_AI_SUBMITTED', { prompt }, { workspaceId });
    },
    [track]
  );

  const trackAiChatStarted = useCallback(
    (collectionId: string, workspaceId?: string) => {
      track('AI_CHAT_STARTED', { collection_id: collectionId }, { workspaceId });
    },
    [track]
  );

  const trackAiChatChangesApplied = useCallback(
    (collectionId: string, changesCount: number, workspaceId?: string) => {
      track(
        'AI_CHAT_CHANGES_APPLIED',
        { collection_id: collectionId, changes_count: changesCount },
        { workspaceId }
      );
    },
    [track]
  );

  const trackAiChatChangesDiscarded = useCallback(
    (collectionId: string, changesCount: number, chatLength: number, workspaceId?: string) => {
      track(
        'AI_CHAT_CHANGES_DISCARDED',
        {
          collection_id: collectionId,
          changes_count: changesCount,
          chat_length: chatLength,
        },
        { workspaceId }
      );
    },
    [track]
  );

  const trackAiChatLimitReached = useCallback(
    (collectionId: string, workspaceId?: string) => {
      track('AI_CHAT_LIMIT_REACHED', { collection_id: collectionId }, { workspaceId });
    },
    [track]
  );

  const trackAiChatUpgradeClicked = useCallback(
    (collectionId: string, workspaceId?: string) => {
      track('AI_CHAT_UPGRADE_CLICKED', { collection_id: collectionId }, { workspaceId });
    },
    [track]
  );

  // ─── Templates / Import ──────────────────────────────────────────────────
  const trackAddTemplateToWorkspace = useCallback(
    (templateId: string, workspaceId?: string) => {
      track('ADD_TEMPLATE_TO_WORKSPACE', { template_id: templateId }, { workspaceId });
    },
    [track]
  );

  const trackPreviewTemplate = useCallback((templateId: string) => {
    track('PREVIEW_TEMPLATE', { template_id: templateId });
  }, [track]);

  const trackSignUpWithTemplate = useCallback((templateId: string) => {
    track('SIGN_UP_WITH_TEMPLATE', { template_id: templateId });
  }, [track]);

  const trackCreateFormFromTypeform = useCallback((workspaceId?: string) => {
    track('CREATE_FORM_FROM_TYPEFORM', {}, { workspaceId });
  }, [track]);

  const trackAddTypeformToWorkspace = useCallback((workspaceId?: string) => {
    track('ADD_TYPEFORM_TO_WORKSPACE', {}, { workspaceId });
  }, [track]);

  const trackSignupFromTypeformImporter = useCallback(() => {
    track('SIGNUP_FROM_TYPEFORM_IMPORTER');
  }, [track]);

  // ─── Revenue ─────────────────────────────────────────────────────────────
  const trackOpenBillingModal = useCallback((workspaceId?: string) => {
    track('OPEN_BILLING_MODAL', {}, { workspaceId });
  }, [track]);

  const trackCheckoutStarted = useCallback(
    (workspaceId?: string, plan?: string) => {
      track('CHECKOUT_STARTED', plan ? { plan } : {}, { workspaceId });
    },
    [track]
  );

  const trackPricingPageViewed = useCallback((source?: string) => {
    track('PRICING_PAGE_VIEWED', { source });
  }, [track]);

  const trackPaywallShown = useCallback(
    (input: { feature: string; planRequired: string; workspaceId?: string }) => {
      track(
        'PAYWALL_SHOWN',
        { feature: input.feature, plan_required: input.planRequired },
        { workspaceId: input.workspaceId }
      );
    },
    [track]
  );

  const trackPaywallUpgradeClicked = useCallback(
    (input: { feature: string; planRequired: string; workspaceId?: string }) => {
      track(
        'PAYWALL_UPGRADE_CLICKED',
        { feature: input.feature, plan_required: input.planRequired },
        { workspaceId: input.workspaceId }
      );
    },
    [track]
  );

  const trackSubscriptionStarted = useCallback(
    (workspaceId?: string, plan?: string, options?: { skipDb?: boolean }) => {
      track('SUBSCRIPTION_STARTED', plan ? { plan } : {}, {
        workspaceId,
        skipDb: options?.skipDb,
      });
    },
    [track]
  );

  const trackSubscriptionCancelled = useCallback(
    (workspaceId?: string, reason?: string) => {
      track('SUBSCRIPTION_CANCELLED', reason ? { reason } : {}, { workspaceId });
    },
    [track]
  );

  const trackFeedbackSurveySubmitted = useCallback(
    (input: { source: string; reason?: string; score?: number; workspaceId?: string }) => {
      track(
        'FEEDBACK_SURVEY_SUBMITTED',
        { source: input.source, reason: input.reason, score: input.score },
        { workspaceId: input.workspaceId }
      );
    },
    [track]
  );

  // ─── Referral ────────────────────────────────────────────────────────────
  const trackReferralLinkCopied = useCallback((code: string) => {
    track('REFERRAL_LINK_COPIED', { code });
  }, [track]);

  const trackReferralLinkShared = useCallback((code: string, channel: string) => {
    track('REFERRAL_LINK_SHARED', { code, channel });
  }, [track]);

  return useMemo(
    () => ({
      // Generic
      track,

      // Auth
      trackSignup,
      trackSignUpFormOpened,
      trackSignupStarted,
      trackSignupValidationError,
      trackSignInStarted,
      trackSignInCompleted,
      trackSignInFailed,
      trackEmailVerified,
      trackResetPasswordRequested,
      trackResetPasswordSuccess,
      trackWorkspaceInviteAccepted,

      // Onboarding
      trackOnboardingStepViewed,
      trackOnboardingStepAnswered,
      trackOnboardingCompleted,
      trackOnboardingSkipped,

      // Editor
      trackEditorFirstEntered,
      trackEditorBouncedWithoutSave,
      trackEditorGetStartedIntro,
      trackEditorGetStartedIntroSkipped,
      trackEditorGetStartedIntroVideoViewed,
      trackHowToTooltipOpened,
      trackHowToTooltipVideoViewed,

      // Form lifecycle
      trackFormCreated,
      trackFormDraftSaved,
      trackFormPublished,
      trackFormFirstPublished,
      trackFormEditedAfterPublish,
      trackAddJourneyItem,
      trackEditJourneyItem,
      trackQuestionEdited,

      // Sharing
      trackOpenShareFormModal,
      trackFormSharedViaLink,
      trackFormEmbedCodeCopied,

      // AI
      trackOpenAiAssistantModal,
      trackCreateFormWithAiOpened,
      trackCreateFormWithAiSubmitted,
      trackAiChatStarted,
      trackAiChatChangesApplied,
      trackAiChatChangesDiscarded,
      trackAiChatLimitReached,
      trackAiChatUpgradeClicked,

      // Templates / Import
      trackAddTemplateToWorkspace,
      trackPreviewTemplate,
      trackSignUpWithTemplate,
      trackCreateFormFromTypeform,
      trackAddTypeformToWorkspace,
      trackSignupFromTypeformImporter,

      // Revenue
      trackOpenBillingModal,
      trackCheckoutStarted,
      trackPricingPageViewed,
      trackPaywallShown,
      trackPaywallUpgradeClicked,
      trackSubscriptionStarted,
      trackSubscriptionCancelled,
      trackFeedbackSurveySubmitted,

      // Referral
      trackReferralLinkCopied,
      trackReferralLinkShared,
    }),
    [
      track,
      trackSignup,
      trackSignUpFormOpened,
      trackSignupStarted,
      trackSignupValidationError,
      trackSignInStarted,
      trackSignInCompleted,
      trackSignInFailed,
      trackEmailVerified,
      trackResetPasswordRequested,
      trackResetPasswordSuccess,
      trackWorkspaceInviteAccepted,
      trackOnboardingStepViewed,
      trackOnboardingStepAnswered,
      trackOnboardingCompleted,
      trackOnboardingSkipped,
      trackEditorFirstEntered,
      trackEditorBouncedWithoutSave,
      trackEditorGetStartedIntro,
      trackEditorGetStartedIntroSkipped,
      trackEditorGetStartedIntroVideoViewed,
      trackHowToTooltipOpened,
      trackHowToTooltipVideoViewed,
      trackFormCreated,
      trackFormDraftSaved,
      trackFormPublished,
      trackFormFirstPublished,
      trackFormEditedAfterPublish,
      trackAddJourneyItem,
      trackEditJourneyItem,
      trackQuestionEdited,
      trackOpenShareFormModal,
      trackFormSharedViaLink,
      trackFormEmbedCodeCopied,
      trackOpenAiAssistantModal,
      trackCreateFormWithAiOpened,
      trackCreateFormWithAiSubmitted,
      trackAiChatStarted,
      trackAiChatChangesApplied,
      trackAiChatChangesDiscarded,
      trackAiChatLimitReached,
      trackAiChatUpgradeClicked,
      trackAddTemplateToWorkspace,
      trackPreviewTemplate,
      trackSignUpWithTemplate,
      trackCreateFormFromTypeform,
      trackAddTypeformToWorkspace,
      trackSignupFromTypeformImporter,
      trackOpenBillingModal,
      trackCheckoutStarted,
      trackPricingPageViewed,
      trackPaywallShown,
      trackPaywallUpgradeClicked,
      trackSubscriptionStarted,
      trackSubscriptionCancelled,
      trackFeedbackSurveySubmitted,
      trackReferralLinkCopied,
      trackReferralLinkShared,
    ]
  );
}
